import { listOfOrder } from "Api/Order";
import BaseButton from "Components/Base/BaseButton";
import TableContainer from "Components/Base/BaseTable";
import {
  checkStatusCodeSuccess,
  notFound,
  Search,
} from "Components/constants/Common";
import {
  AUTH_USER_NAME_KEY,
  ORDER_ID,
  ORDER_LABEL,
  PAID_LABEL,
  PARTIALLY_PAID_LABEL,
  PARTIALLY_REFUNDED_LABEL,
  PAY_LABEL,
  PAYMENT_STATUS_KEY,
  REFUNDED_LABEL,
} from "Components/constants/Order";
import {
  AmountLabel,
  OrderDateKey,
  OrderDateLabel,
  QuantityKey,
  QuantityLabel,
} from "Components/constants/PurchaseOrder";
import {
  ActionKey,
  ActionLabel,
  NewLabel,
  StatusLabel,
} from "Components/constants/Role";
import { CustomerNameLabel } from "Components/constants/Staff";
import moment from "moment";
import { SetStateAction, useEffect, useMemo, useState } from "react";

import {
  ButtonGroup,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import { RiSearchLine } from "react-icons/ri";
import { useNavigate, useParams } from "react-router-dom";

import { RiTruckLine } from "react-icons/ri";
import TransportModal from "./TransportForm";
import Loader from "Components/Base/BaseLoader";
import { gen_ord_invoice } from "Api/Invoice";

const Order = () => {
  document.title = ORDER_LABEL;
  const navigate = useNavigate();

  const [staffData, setstaffData] = useState<Array<any>>([]);
  const [staffSearchData, setstaffSearchData] = useState<Array<any>>([]);
  const [loader, setLoader] = useState<boolean>(false);
  const [totalNumberOfRows, setTotalNumberOfRows] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [columnName, setColumnName] = useState("id");
  const [sortOrder, setSortOrder] = useState("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const [customPageSize, setCustomPageSize] = useState(10);
  const [searchValue, setSearchValue] = useState("");
  const [pageLoader, setPageLoader] = useState<boolean>(false);

  const [isTransportModalOpen, setIsTransportModalOpen] =
    useState<boolean>(false);
  const [orderId, setOrderId] = useState(null);

  const toggleTransportModal = () => {
    setIsTransportModalOpen(!isTransportModalOpen);
  };

  const handleFetchSorting = (
    page: any,
    id: SetStateAction<string>,
    order: SetStateAction<string>
  ) => {
    setCurrentPage(page);
    if(id === "quantity"){
      setColumnName("orderItem.quantity")
    } else {
      setColumnName(id);
    }
    setSortOrder(order);
  };
  const handleFetchData = (page: any) => {
    setCurrentPage(page);
  };

  const handleSearchValueChange = (value: any) => {
    if (value !== searchValue) {
      setCurrentPage(1);
      setSearchValue(value);
    }
  };

  const handleDownload = async (row: any) => {
    setPageLoader(true);
    try {
      const response = await gen_ord_invoice(row.id, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response], { type: "application/pdf" }));
      
      const link = document.createElement("a");
      link.href = url;
      link.download = `order_${row.order_draft_id || row.id}.pdf`;
      link.click();
      
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error(error);
    } finally {
      setPageLoader(false);
    }
  };

  const orderColumns = useMemo(
    () => [
      {
        header: ORDER_ID,
        accessorKey: "order_draft_id",
        cell: (cell: any) => cell.getValue(),
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: CustomerNameLabel,
        accessorKey: AUTH_USER_NAME_KEY,
        enableColumnFilter: false,
      },
      {
        header: QuantityLabel,
        accessorKey: QuantityKey,
        cell: (cell: any) => {
          const totalQuantity = cell.row?.original?.orderItem?.reduce(
            (sum: number, item: any) => {
              return sum + (item?.quantity || 0);
            },
            0
          );

          return (
            <div className="list-group list-group-flush p-1">
              <span className="list-group-item p-0">{totalQuantity}</span>
            </div>
          );
        },
        enableSorting: true,
        enableColumnFilter: false,
      },

      {
        header: StatusLabel,
        accessorKey: PAYMENT_STATUS_KEY,
        cell: (cell: any) => {
          const statusColorMap: { [key: string]: string } = {
            Pending: "bg-warning",
            "Partially Paid": "bg-info",
            Paid: "bg-success",
            "Partially Refunded": "bg-primary",
            Returned: "bg-danger",
          };

          const paymentStatus = cell?.row?.original?.payment_status;

          return (
            <div>
              <span
                className={`badge ${
                  statusColorMap[paymentStatus] || "bg-primary"
                }`}
              >
                {paymentStatus}
              </span>
            </div>
          );
        },
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        header: AmountLabel,
        accessorKey: "total_price",
        cell: (cell: any) => {
          return (
            <div className="list-group bg-none list-group-flush p-1">
              <span>
                <i className="mdi mdi-currency-rupee"></i>
                {cell.row?.original?.total_price?.toFixed(2)}
              </span>
            </div>
          );
        },
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        header: OrderDateLabel,
        accessorKey: OrderDateKey,
        cell: (cell: any) => {
          return (
            <div>
              <span>
                {moment(cell?.row?.original?.order_date).format("ll")}
              </span>
            </div>
          );
        },
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        header: ActionLabel,
        accessorKey: ActionKey,
        cell: ({ row }: { row: any }) => {
          const rowId = row.original.id;
          const isPending = row.original.payment_status === "Pending";
          const isPartiallyPaid =
            row.original.payment_status === PARTIALLY_PAID_LABEL;
          const isPaid = row.original.payment_status === PAID_LABEL;
          const isRefunded = row.original.payment_status === REFUNDED_LABEL;
          const isPartiallyRefunded =
            row.original.payment_status === PARTIALLY_REFUNDED_LABEL;
          const rowData = row.original;
          return (
            <div className="text-center enable-action">
              <ButtonGroup>
                <UncontrolledDropdown direction="start">
                  <DropdownToggle
                    tag="button"
                    className="btn btn-link border px-2 py-1 shadow-none text-dark"
                    type="button"
                    id={`dropdown-${rowId}`} // Unique ID for each dropdown
                  >
                    <i className="mdi mdi-dots-vertical"></i>
                    <i className="mdi mdi-chevron-down ms-1"></i>
                  </DropdownToggle>
                  <DropdownMenu>
                    <DropdownItem
                      key={`${rowId}-pay`} // Unique key for each item
                      onClick={() => navigate(`/add-order-payment/${rowId}`)}
                      disabled={isPaid || isRefunded || isPartiallyRefunded}
                      className={
                        isPaid || isRefunded || isPartiallyRefunded
                          ? "text-muted"
                          : ""
                      }
                    >
                      {PAY_LABEL}
                    </DropdownItem>
                    <DropdownItem
                      key={`${rowId}-refund`}
                      onClick={() => navigate(`/return-order/${rowId}`)}
                      disabled={isPending || isPartiallyPaid || isRefunded}
                    >
                      {REFUNDED_LABEL}
                    </DropdownItem>

                    <DropdownItem
                      key={`${rowId}-transporatation`}
                      onClick={() => {
                        setIsTransportModalOpen(!isTransportModalOpen);
                        setOrderId(row?.original?.id);
                      }}
                    >
                      Transportation
                    </DropdownItem>
                    <DropdownItem
                      key={`${rowId}-email`}
                      onClick={() => navigate(`/compose/${rowId}`)}
                    >
                      Email
                    </DropdownItem>
                    <DropdownItem
                      key={`${rowId.id}-download`}
                      onClick={() => handleDownload(rowData)}
                    >
                      Download
                    </DropdownItem>
                  </DropdownMenu>
                </UncontrolledDropdown>
              </ButtonGroup>
            </div>
          );
        },
        enableSorting: false,
        enableColumnFilter: false,
      },
    ],
    []
  );

  const fetchStaffData = () => {
    setLoader(true);
    const payload = {
      sortKey: columnName,
      sortValue: sortOrder,
      page: currentPage,
      limit: customPageSize,
    };
    listOfOrder(payload)
      .then((response) => {
        if (checkStatusCodeSuccess(response.statusCode)) {
          setstaffData(response.data.listOfOrder);
          setTotalRecords(response.data.totalItems);
          setTotalPages(response?.data?.totalPages);
          setTotalNumberOfRows(response?.data?.numberOfRows);
          if (searchValue) {
            fetchSearchStaffData();
          }
        }
      })
      .catch((error) => {
        return error;
      })
      .finally(() => {
        setLoader(false);
      });
  };
  const fetchSearchStaffData = () => {
    setLoader(true);
    const payload = {
      sortKey: columnName,
      sortValue: sortOrder,
      page: currentPage,
      search: searchValue,
      limit: customPageSize,
    };
    listOfOrder(payload)
      .then((response) => {
        if (checkStatusCodeSuccess(response.statusCode)) {
          setstaffSearchData(response.data.listOfOrder);
          setTotalRecords(response.data.totalItems);
          setTotalPages(response?.data?.totalPages);
          setTotalNumberOfRows(response?.data?.numberOfRows);
        }
      })
      .catch((error) => {
        setstaffSearchData([]);
        return error;
      })
      .finally(() => {
        setLoader(false);
      });
  };

  useEffect(() => {
    if (searchValue === "") {
      setCurrentPage(1);
    }
  }, [searchValue]);

  useEffect(() => {
    if (searchValue) {
      fetchSearchStaffData();
    } else {
      fetchStaffData();
    }
  }, [currentPage, customPageSize, sortOrder, columnName, searchValue]);

  return (
    <div className="page-content">
      {pageLoader && <Loader />}
      <div className="flex-grow-1 mx-3">
        <div className="mb-1 mx-2 d-flex justify-content-between align-items-center">
          <span className="fs-4 d-flex gap-4">{ORDER_LABEL}</span>
          <div className="d-flex align-items-center gap-2">
            <BaseButton
              className="btn btn-success"
              onClick={() => navigate("/add-order")}
              children={
                <>
                  <i className="mdi mdi-plus" /> {NewLabel}
                </>
              }
            />
          </div>
        </div>
        <div className="card py-2 px-4 overflow-x-auto">
          {loader || staffData.length > 0 ? (
            <TableContainer
              customPageSize={customPageSize}
              setCustomPageSize={setCustomPageSize}
              isGlobalFilter={true}
              columns={orderColumns}
              data={searchValue ? staffSearchData : staffData || []}
              tableClass="table bg-white"
              manualPagination={true}
              manualFiltering={true}
              tableLoader={loader}
              hasManualPagination={true}
              SearchPlaceholder={Search}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              totalPages={totalPages}
              totalRecords={totalRecords}
              trClass="cursor-pointer"
              setDrawerOpen={(value: any, data: any) =>
                navigate(`/view-order/${data?.id}`)
              }
              onSearch={handleSearchValueChange}
              totalNumberOfRows={totalNumberOfRows}
              fetchData={handleFetchData}
              fetchSortingData={handleFetchSorting}
            />
          ) : (
            <div className="py-4 text-center">
              <div>
                <span className="fs-1 text-success">
                  <RiSearchLine />
                </span>
              </div>
              <div className="mt-4">
                <h5>{notFound.dataNotFound}</h5>
                <p className="text-muted">{notFound.thereIsNoDataFound}</p>
              </div>
            </div>
          )}
        </div>
        <TransportModal
          isTransportModalOpen={isTransportModalOpen}
          setIsTransportModalOpen={setIsTransportModalOpen}
          toggleTransportModal={toggleTransportModal}
          orderId={Number(orderId)}
        />
      </div>
    </div>
  );
};

export default Order;
